import { useTracking } from "netbank-shared/src/hooks";
import { TrackingAction, TrackingCategory } from "netbank-shared/src/libs/models/Tracking";
import React, { forwardRef, HTMLAttributes, useEffect, useState } from "react";
import styles from "./Button.scss";
import classnames from "classnames";

interface IButtonProps extends HTMLAttributes<HTMLButtonElement> {
  type?: "primary" | "secondary" | "text";
  disabled?: boolean;
  fullWidth?: boolean;
  iconPrefix?: string;
  iconSuffix?: string;
  loading?: boolean;
  title?: string;
  trackingAction?: TrackingAction;
  trackingCategory?: TrackingCategory;
  trackingPrefix?: string;
  trackingValue?: number;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => Promise<boolean> | boolean | Promise<void> | void;
}

export const Button = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      className,
      disabled,
      fullWidth,
      iconPrefix,
      iconSuffix,
      loading,
      onClick,
      title,
      trackingCategory,
      trackingAction,
      trackingPrefix,
      trackingValue,
      type = "primary",
      ...rest
    },
    ref
  ) => {
    const dataLayer = useTracking((window as any).dataLayer);

    const [isLoading, setIsLoading] = useState(loading);

    useEffect(() => {
      setIsLoading(loading);
    }, [loading]);

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isLoading || !onClick || disabled) return;

      if (trackingCategory && trackingAction) {
        dataLayer.pushInteraction(trackingCategory, trackingAction, trackingPrefix, trackingValue);
      }

      try {
        setIsLoading(true);
        await onClick(e);
      } finally {
        if (!loading) setIsLoading(false);
      }
    };

    const classes = classnames(styles.button, className, {
      [styles.fullWidth]: fullWidth,
      [styles.disabled]: disabled,
      [styles.loading]: loading || isLoading,
      [styles.primary]: type === "primary",
      [styles.text]: type === "text",
    });

    return (
      <button
        ref={ref}
        className={classes}
        onClick={handleClick}
        aria-disabled={loading || isLoading || disabled}
        aria-busy={loading || isLoading}
        aria-label={title}
        type="button"
        {...rest}
      >
        <div className={styles.inner}>
          {(loading || isLoading) && <div className={styles.spinner} />}
          {iconPrefix && <img src={iconPrefix} alt="" />}
          {title && <span>{title}</span>}
          {iconSuffix && <img src={iconSuffix} alt="" />}
        </div>
      </button>
    );
  }
);
